import React from 'react';
import { useIntl } from 'react-intl';
import type { OnboardingAction, OnboardingChecklist } from 'fym-shared/src/music24/onboardingChecklist';
import { TypedMemo } from '../../common/types';
import { Panel } from '../../music24/components/primitives/Panel';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { ChecklistProgress } from './ChecklistProgress';
import { ChecklistItemRow } from './ChecklistItemRow';
import { checklistMessages as messages } from './checklistMessages';
import { useChecklistNudge } from './useChecklistNudge';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; each part uses its own slice of it */
import styles from './FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChecklistSetupListProps {
  checklist: OnboardingChecklist;
  subtitle: string;
  readyAssets: number;
  onAction: (action: OnboardingAction) => void;
  /** False for a member: the plan pick shows who can do it in place of its button. */
  canManage: boolean;
  /** "Show the checklist again" after a dismiss (design d6). Only the owner gets it. */
  onRestore?: () => void;
}

/** Settings -> Setup: the full list with each body and button (design: onboarding d6). */
const ChecklistSetupListComponent: React.FC<ChecklistSetupListProps> = (props) => {
  const { checklist, subtitle, readyAssets, onAction, canManage, onRestore } = props;
  const intl = useIntl();
  const runNudge = useChecklistNudge();
  const nextIndex = checklist.items.findIndex((item) => !item.done);
  const ownerOnlyNote = intl.formatMessage(messages.setupOwnerOnly);

  return (
    <Panel className={styles.card} data-testid="checklist-setup-list">
      <div className={styles.setupHead}>
        <ChecklistProgress done={checklist.doneCount} total={checklist.total} />
        <div className={styles.headText}>
          <h3>{intl.formatMessage(messages.title)}</h3>
          <small>{subtitle}</small>
        </div>
        {onRestore !== undefined ? (
          <SMLButton onClick={onRestore}>{intl.formatMessage(messages.setupShow)}</SMLButton>
        ) : null}
      </div>
      <ul className={styles.items}>
        {checklist.items.map((item, index) => (
          <ChecklistItemRow
            key={item.key}
            item={item}
            isNext={index === nextIndex}
            variant="full"
            readyAssets={readyAssets}
            onAction={onAction}
            onNudge={runNudge}
            lockedNote={!canManage && item.action.screen === 'plans' ? ownerOnlyNote : undefined}
          />
        ))}
      </ul>
    </Panel>
  );
};

export const ChecklistSetupList = TypedMemo(ChecklistSetupListComponent);
